import { useEffect, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import axios from "axios"
import Swal from "sweetalert2"
import moment from "moment"
import { toast } from "react-toastify"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowLeft, faEnvelopeOpenText, faTrash } from "@fortawesome/free-solid-svg-icons"
import { baseUrl } from "../../config/baseUrl"
import PermissionGuard from "../PermissionGuard/PermissionGuard"
import { hasPermission, PERMISSIONS } from "../../utils/permissions"

const STATUS_OPTIONS = ["open", "pending", "resolved"]

export default function MessageView() {
  const { id } = useParams()
  const navigate = useNavigate()
  const canEditContactQuery = hasPermission(PERMISSIONS.CONTACT_QUERY_EDIT)
  const [message, setMessage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [statusLoading, setStatusLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  useEffect(() => {
    const fetchMessage = async () => {
      try {
        setLoading(true)
        const response = await axios.get(`${baseUrl}/contact-query/${id}`, {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        })

        if (response?.data?.status) {
          setMessage(response.data.data)
        } else {
          toast.error(response?.data?.message || "Failed to fetch contact query")
          navigate("/contact-queries")
        }
      } catch (error) {
        console.error("Error fetching contact query:", error)
        toast.error(error.response?.data?.message || "Failed to fetch contact query")
        navigate("/contact-queries")
      } finally {
        setLoading(false)
      }
    }

    fetchMessage()
  }, [id, navigate])

  const handleStatusChange = async (nextStatus) => {
    try {
      setStatusLoading(true)
      const response = await axios.patch(
        `${baseUrl}/contact-query/${id}/status`,
        { status: nextStatus },
        {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        },
      )

      if (response?.data?.status) {
        setMessage((prev) => ({ ...prev, status: nextStatus }))
        toast.success("Status updated successfully")
      } else {
        toast.error(response?.data?.message || "Failed to update status")
      }
    } catch (error) {
      console.error("Error updating contact query status:", error)
      toast.error(error.response?.data?.message || "Failed to update status")
    } finally {
      setStatusLoading(false)
    }
  }

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: "Delete query?",
      text: "This contact query will be removed permanently.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it",
    })

    if (!result.isConfirmed) return

    try {
      setDeleteLoading(true)
      const response = await axios.delete(`${baseUrl}/contact-query/${id}`, {
        headers: {
          Authorization: localStorage.getItem("token"),
        },
      })

      if (response?.data?.status) {
        toast.success("Contact query deleted successfully")
        navigate("/contact-queries")
      } else {
        toast.error(response?.data?.message || "Failed to delete contact query")
      }
    } catch (error) {
      console.error("Error deleting contact query:", error)
      toast.error(error.response?.data?.message || "Failed to delete contact query")
    } finally {
      setDeleteLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="main-sec">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 mb-0">Loading contact query...</p>
        </div>
      </div>
    )
  }

  const effectiveStatus = message?.status || "open"

  return (
    <section className="main-sec">
      <div className="container-fluid">
        <div className="page-header">
          <div>
            <h2 className="page-title">
              <FontAwesomeIcon icon={faEnvelopeOpenText} className="me-2" />
              Contact Query Details
            </h2>
            <p className="page-subtitle">Review the full submitted message and manage its status.</p>
          </div>
          <div className="header-actions">
            <Link to="/contact-queries" className="btn-back">
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Back
            </Link>
            <PermissionGuard permission={PERMISSIONS.CONTACT_QUERY_DELETE}>
              <button className="btn-delete" onClick={handleDelete} disabled={deleteLoading}>
                <FontAwesomeIcon icon={faTrash} className="me-2" />
                {deleteLoading ? "Deleting..." : "Delete"}
              </button>
            </PermissionGuard>
          </div>
        </div>

        <div className="detail-card">
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">Name</span>
              <span className="detail-value">{message?.name || "N/A"}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Email</span>
              <span className="detail-value">
                {message?.email ? (
                  <a href={`mailto:${message.email}`} className="text-decoration-none">
                    {message.email}
                  </a>
                ) : (
                  "N/A"
                )}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Subject</span>
              <span className="detail-value">{message?.subject || "N/A"}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Submitted At</span>
              <span className="detail-value">
                {message?.createdAt ? moment(message.createdAt).format("DD MMM YYYY, hh:mm A") : "N/A"}
              </span>
            </div>
            <div className="detail-item status-item">
              <span className="detail-label">Status</span>
              <select
                className={`form-select status-select status-${effectiveStatus}`}
                value={effectiveStatus}
                onChange={(e) => handleStatusChange(e.target.value)}
                disabled={statusLoading || !canEditContactQuery}
              >
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="message-panel">
            <span className="detail-label">Message</span>
            <div className="message-box">{message?.message || "N/A"}</div>
          </div>
        </div>
      </div>

      <style>{`
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          gap: 16px;
          flex-wrap: wrap;
        }

        .page-title {
          margin: 0;
          font-size: 28px;
          font-weight: 700;
          color: #1f2937;
        }

        .page-subtitle {
          margin: 6px 0 0;
          color: #6b7280;
        }

        .header-actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .btn-back,
        .btn-delete {
          border: none;
          border-radius: 10px;
          padding: 10px 18px;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.3s ease;
        }

        .btn-back {
          background: #e5e7eb;
          color: #111827;
        }

        .btn-delete {
          background: #fee2e2;
          color: #b91c1c;
        }

        .btn-back:hover {
          background: #d1d5db;
          color: #111827;
        }

        .btn-delete:hover:not(:disabled) {
          background: #ef4444;
          color: white;
        }

        .detail-card {
          background: white;
          border-radius: 18px;
          padding: 28px;
          box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);
        }

        .detail-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 18px;
          margin-bottom: 24px;
        }

        .detail-item {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 18px;
          background: #f8fafc;
          border-radius: 14px;
        }

        .status-item {
          align-self: start;
        }

        .detail-label {
          font-size: 13px;
          font-weight: 700;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }

        .detail-value {
          font-size: 16px;
          font-weight: 600;
          color: #0f172a;
          word-break: break-word;
        }

        .status-select {
          max-width: 180px;
          font-weight: 700;
          border-radius: 999px;
          border: none;
          box-shadow: none !important;
        }

        .status-open {
          background-color: #dbeafe;
          color: #1d4ed8;
        }

        .status-pending {
          background-color: #fef3c7;
          color: #b45309;
        }

        .status-resolved {
          background-color: #d1fae5;
          color: #047857;
        }

        .message-panel {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .message-box {
          background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 20px;
          white-space: pre-wrap;
          line-height: 1.7;
          color: #334155;
          min-height: 180px;
        }

        @media (max-width: 768px) {
          .detail-card {
            padding: 20px;
          }

          .header-actions {
            width: 100%;
          }

          .btn-back,
          .btn-delete {
            width: 100%;
            text-align: center;
          }
        }
      `}</style>
    </section>
  )
}
